import { Download } from 'lucide-react'
import { Badge, Button, Card, CardBody, CardHeader, ProgressBar, Table, type Column } from '@/components/ui'
import { QueryBoundary, StatTile } from '@/components/domain'
import { formatDayLabel, formatNumber } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import type { Invoice } from '@/types'
import { useBilling } from '../hooks'

export function BillingSettings() {
  const t = useT()
  const { data, isPending, error, refetch } = useBilling()

  const columns: Column<Invoice>[] = [
    { id: 'period', header: t('billing.col.period'), cell: (row) => row.period },
    {
      id: 'amount',
      header: t('billing.col.amount'),
      align: 'right',
      cell: (row) => `${row.currency} ${formatNumber(row.amount)}`,
    },
    {
      id: 'status',
      header: t('billing.col.status'),
      width: 'w-24',
      cell: (row) => (
        <Badge tone={row.status === 'paid' ? 'success' : row.status === 'due' ? 'warning' : 'danger'}>
          {row.status}
        </Badge>
      ),
    },
    {
      id: 'download',
      header: '',
      align: 'right',
      width: 'w-28',
      cell: () => (
        <Button size="sm" variant="ghost" leftIcon={<Download className="h-4 w-4" />}>
          {t('common.pdf')}
        </Button>
      ),
    },
  ]

  return (
    <QueryBoundary isLoading={isPending} error={error} onRetry={refetch}>
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <StatTile label={t('billing.stat.plan')} value={data.plan} hint={t('billing.stat.renews', { date: formatDayLabel(data.renewsAt) })} />
            <StatTile label={t('billing.stat.seats')} value={`${data.seats.used} / ${data.seats.total}`} />
            <StatTile
              label={t('billing.stat.minutesUsed')}
              value={formatNumber(data.minutesUsed)}
              hint={t('billing.stat.ofIncluded', { count: formatNumber(data.minutesIncluded) })}
            />
          </div>

          <Card>
            <CardHeader
              title={t('billing.usageTitle')}
              description={t('billing.usageDesc')}
              action={<Button size="sm">{t('billing.changePlan')}</Button>}
            />
            <CardBody className="space-y-2">
              <ProgressBar
                value={data.minutesUsed / data.minutesIncluded}
                tone={data.minutesUsed / data.minutesIncluded > 0.85 ? 'warning' : 'brand'}
                label={t('billing.minutesUsedLabel')}
              />
              <p className="text-sm text-fg-muted">
                {t('billing.minutesRemaining', { count: formatNumber(data.minutesIncluded - data.minutesUsed) })}
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={t('billing.invoices')} />
            <Table columns={columns} rows={data.invoices} rowKey={(row) => row.id} />
          </Card>
        </div>
      )}
    </QueryBoundary>
  )
}
